'use client';

import { useEffect, useMemo, useState } from 'react';

import { api } from '@/lib/convex';
import { useQuery } from '@/lib/server-seed/seeded-convex-hooks';
import { resolveResearchReportMarkdownSource } from '@/components/chat/tool-calls/research-report-markdown-source';

export interface AuthorizedWorkspaceMarkdownState {
  error: boolean;
  key: string | null;
  loading: boolean;
  text: string | null;
}

type RemoteMarkdownState = {
  contentKey: string;
  error: boolean;
  key: string;
  text: string | null;
};

/**
 * Signed download URLs rotate while the file revision (`contentKey`) stays
 * the same. Keep showing the loaded text for that revision while the new URL
 * refetches, so the card does not drop back to its loading shape.
 */
export function resolveRemoteMarkdownState(
  contentKey: string,
  requestKey: string,
  remoteState: RemoteMarkdownState
): AuthorizedWorkspaceMarkdownState {
  if (remoteState.key === requestKey) {
    return {
      error: remoteState.error,
      key: contentKey,
      loading: false,
      text: remoteState.text,
    };
  }
  if (remoteState.contentKey === contentKey && remoteState.text !== null) {
    return {
      error: false,
      key: contentKey,
      loading: false,
      text: remoteState.text,
    };
  }
  return { error: false, key: contentKey, loading: true, text: null };
}

export function useAuthorizedWorkspaceMarkdown(args: {
  enabled: boolean;
  key: string | null;
  legacyUrl?: string | null;
  orgSlug: string;
  path?: string | null;
}): AuthorizedWorkspaceMarkdownState {
  const queryEnabled =
    args.enabled && args.orgSlug.length > 0 && Boolean(args.path);
  const path = args.path;
  // Seeded: a server-rendered thread paints the document without waiting
  // for the socket; the live subscription takes over when it delivers.
  const readResult = useQuery(
    api.vfs.public.readFile,
    queryEnabled && path ? { orgSlug: args.orgSlug, path } : 'skip'
  );
  const source = useMemo(
    () =>
      resolveResearchReportMarkdownSource({
        readResult,
        legacyUrl: args.legacyUrl,
        queryEnabled,
      }),
    [args.legacyUrl, queryEnabled, readResult]
  );
  const [remoteState, setRemoteState] = useState<RemoteMarkdownState>({
    contentKey: '',
    error: false,
    key: '',
    text: null,
  });

  useEffect(() => {
    if (!args.enabled || !args.key || source.kind !== 'remote') return;
    const contentKey = args.key;
    const requestKey = `${contentKey}:${source.url}`;
    const controller = new AbortController();
    void fetch(source.url, { signal: controller.signal })
      .then((response) => {
        if (!response.ok) throw new Error('WORKSPACE_MARKDOWN_LOAD_FAILED');
        return response.text();
      })
      .then((text) =>
        setRemoteState({ contentKey, error: false, key: requestKey, text })
      )
      .catch(() => {
        if (!controller.signal.aborted) {
          setRemoteState({
            contentKey,
            error: true,
            key: requestKey,
            text: null,
          });
        }
      });
    return () => controller.abort();
  }, [args.enabled, args.key, source]);

  if (!args.enabled || !args.key) {
    return { error: false, key: null, loading: false, text: null };
  }
  if (source.kind === 'inline') {
    return { error: false, key: args.key, loading: false, text: source.text };
  }
  if (source.kind === 'error') {
    return { error: true, key: args.key, loading: false, text: null };
  }
  if (source.kind === 'loading') {
    return { error: false, key: args.key, loading: true, text: null };
  }

  return resolveRemoteMarkdownState(
    args.key,
    `${args.key}:${source.url}`,
    remoteState
  );
}
